import { memo, useCallback, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { DemoChip } from '@frontend/analytics-shared';
import { Chart, CustomLegendsData, FormatValue, PieChartData } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { trackingIds } from '../../tracking-ids';
import { CompareLocationsButton } from '../compare-locations-button';
import { useGetPractitionerMetric } from './hooks/use-get-practitioner-metric';
import { usePaNavigate } from './hooks/use-pa-navigate';
import { usePracticeAnalyticsShallowStore } from './hooks/use-practice-analytics-store';

type ChartData = {
  acceptedPercent: PieChartData;
  diagnosedPercent: PieChartData;
  unscheduledTreatment: PieChartData;
  totals: {
    accepted: number;
    diagnosed: number;
    unscheduledProduction: {
      hygiene: number;
      restorative: number;
    };
  };
};

type RenderChart = {
  commonTooltipLabel?: string;
  customLegends: CustomLegendsData;
  data?: PieChartData;
  formatter: FormatValue;
  tooltipTitle?: string;
};

const colors = {
  accepted: theme.colors.primary20,
  diagnosed: theme.colors.secondary.seaweed30,
  hygiene: theme.colors.warning50,
  restorative: theme.colors.secondary.eggplant30,
  skipTipRemaining: theme.colors.neutral20,
};

const customLegendsIds = Object.keys(colors);

const defaultChartData: PieChartData = {
  groups: [],
};

export const PractitionerAnalysisMetric = memo(
  ({ clickNoop, onFetchStateChange }: PracticeAnalyticsTypes.MetricProps) => {
    const { t } = useTranslation('analytics');
    const navigate = usePaNavigate();
    const { filters, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore('filters', 'showDemoChipAndBanner');
    const { aggregatedPractitionerAnalysisData, isLoading } = useGetPractitionerMetric();

    const multipleLocationsSelected = (filters.locations?.length || 0) > 1;

    const chartData: ChartData | null = useMemo(() => {
      if (!aggregatedPractitionerAnalysisData) {
        return null;
      }

      const { hygieneTreatmentPlan, restorativeTreatmentPlan } = aggregatedPractitionerAnalysisData;
      const totalAccepted = hygieneTreatmentPlan.totals.accepted + restorativeTreatmentPlan.totals.accepted;
      const totalDiagnosed = hygieneTreatmentPlan.totals.diagnosed + restorativeTreatmentPlan.totals.diagnosed;

      let acceptedPercent = Math.round((totalAccepted / totalDiagnosed) * 100);
      if (isNaN(acceptedPercent) || !isFinite(acceptedPercent)) {
        acceptedPercent = 0;
      }

      const diagnosedPercent = Math.round(
        ((hygieneTreatmentPlan.totals.diagnosedPercent + restorativeTreatmentPlan.totals.diagnosedPercent) / 2) * 100
      );
      const totalUnscheduledTreatment =
        hygieneTreatmentPlan.totals.unscheduledTreatment + restorativeTreatmentPlan.totals.unscheduledTreatment;

      const totals = {
        accepted: totalAccepted,
        diagnosed: totalDiagnosed,
        unscheduledProduction: {
          hygiene: hygieneTreatmentPlan.totals.unscheduledTreatment,
          restorative: restorativeTreatmentPlan.totals.unscheduledTreatment,
        },
      };

      return {
        acceptedPercent: {
          centerMetric: formatters.appendPercent(acceptedPercent),
          groups: [
            {
              name: 'accepted',
              value: acceptedPercent,
            },
            {
              name: 'skipTipRemaining',
              value: 100 - acceptedPercent,
            },
          ],
        },
        diagnosedPercent: {
          centerMetric: formatters.appendPercent(diagnosedPercent),
          groups: [
            {
              name: 'diagnosed',
              value: diagnosedPercent,
            },
            {
              name: 'skipTipRemaining',
              value: 100 - diagnosedPercent,
            },
          ],
        },
        totals,
        unscheduledTreatment: {
          centerMetric: `$${formatters.shortenNumber(totalUnscheduledTreatment)}`,
          groups: [
            {
              name: 'hygiene',
              value: hygieneTreatmentPlan.totals.unscheduledTreatment,
            },
            {
              name: 'restorative',
              value: restorativeTreatmentPlan.totals.unscheduledTreatment,
            },
          ],
        },
      };
    }, [aggregatedPractitionerAnalysisData]);

    const labels = useMemo(
      () => ({
        accepted: t('Patients Accepted'),
        diagnosed: t('Patients Diagnosed '),
        hygiene: t('Hygiene unscheduled treatment'),
        restorative: t('Restorative unscheduled treatment'),
      }),
      [t]
    );

    const renderChart = useCallback(
      ({ commonTooltipLabel, customLegends, data, formatter, tooltipTitle }: RenderChart) => {
        return (
          <div css={styles.chartWrapper}>
            <Chart.PieChart
              appearance={{
                collectiveTooltip: true,
                customTooltipTitle: () => tooltipTitle,
                minWidth: 240,
              }}
              commonTooltipLabel={commonTooltipLabel}
              customLegendsIds={customLegendsIds}
              data={data ?? defaultChartData}
              formatValue={formatter}
            />
            <Chart.Legends customData={customLegends} layout='vertical' />
          </div>
        );
      },
      []
    );

    useEffect(() => {
      onFetchStateChange?.(isLoading);
    }, [isLoading]);

    return (
      <Chart
        colors={colors}
        emptyStateConfig={{
          hideEmptyState:
            !!chartData?.totals.accepted ||
            !!chartData?.totals.diagnosed ||
            !!chartData?.totals.unscheduledProduction.hygiene ||
            !!chartData?.totals.unscheduledProduction.restorative,
        }}
        isLoading={isLoading}
        labels={labels}
        onClick={
          clickNoop
            ? undefined
            : () => {
                navigate({
                  to: '/analytics/practice/practitioner-analysis',
                });
              }
        }
        trackingId={trackingIds.practiceAnalytics.practitionerAnalysisChart}
      >
        <Chart.Header
          bottomElement={multipleLocationsSelected ? <CompareLocationsButton /> : null}
          leftElement={showDemoChipAndBanner ? <DemoChip /> : null}
          title={t('Practitioner Analysis')}
        />
        <Chart.HorizontalContainer grid={3} style={{ marginBottom: 0 }}>
          {renderChart({
            commonTooltipLabel: t('Patients'),
            customLegends: {
              diagnosed: {
                label: t('Patients Diagnosed'),
                value: chartData?.totals?.diagnosed,
              },
            },
            data: chartData?.diagnosedPercent,
            formatter: formatters.appendPercent,
            tooltipTitle: labels.diagnosed,
          })}

          {renderChart({
            commonTooltipLabel: t('Patients'),
            customLegends: {
              accepted: {
                label: t('Patients Accepted'),
                value: chartData?.totals?.accepted,
              },
            },
            data: chartData?.acceptedPercent,
            formatter: formatters.appendPercent,
            tooltipTitle: labels.accepted,
          })}

          {renderChart({
            customLegends: {
              hygiene: {
                label: labels['hygiene'],
                value: formatters.shortenCurrency(chartData?.totals?.unscheduledProduction?.hygiene),
              },
              restorative: {
                label: labels['restorative'],
                value: formatters.shortenCurrency(chartData?.totals?.unscheduledProduction?.restorative),
              },
            },
            data: chartData?.unscheduledTreatment,
            formatter: (value) => formatters.shortenCurrency(value),
            tooltipTitle: t('Unscheduled Treatment'),
          })}
        </Chart.HorizontalContainer>
      </Chart>
    );
  }
);

PractitionerAnalysisMetric.displayName = 'PractitionerAnalysisMetric';

const styles = {
  chartWrapper: css`
    align-items: center;
    display: flex;
    flex-direction: column;

    .sneak-peak-item.vertical {
      align-items: flex-start;
      gap: ${theme.spacing(1)};
    }
  `,
};
